import { useEffect, useRef, useState } from 'react';
import { MotionConfig } from 'framer-motion';
import { BookOpen } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { toast } from 'react-hot-toast';
import { CopilotCommand, useCopilotCommand } from '@/features/copilot/copilotBus';
import { useKnowledge } from './hooks/useKnowledge';
import { USE_MOCKS } from '@/lib/mockConfig';
import { AddProductModal } from './components/AddProductModal';
import { ConnectSheetModal } from './components/ConnectSheetModal';
import { FaqSection } from './components/FaqSection';
import { BusinessKnowledgeCard } from './components/BusinessKnowledgeCard';
import { ImportCsvModal } from './components/ImportCsvModal';
import { KnowledgeSummaryStrip } from './components/KnowledgeSummaryStrip';
import { ProductSourcesGrid } from './components/ProductSourcesGrid';
import { ProductsTable } from './components/ProductsTable';
import {
  countInKnowledgeBase,
  countProductsMissingDescriptions,
  seedFaqs,
  seedProducts,
  type FaqEntry,
  type Product,
} from './mockKnowledge';

type ProductModalMode = 'add' | 'edit' | 'csv' | 'sheet' | null;

/**
 * Merchant-facing Knowledge Base page: what the AI assistant can draw on to
 * answer customers — synced/manual product catalog plus FAQ & policy
 * knowledge. Products are loaded once, then the whole list is auto-saved
 * (debounced) on every change; FAQs are saved per item.
 *
 * ⚠️ THE AUTO-SAVE WRITES WHATEVER IS ON SCREEN. So the page starts empty (sample
 * data only in a mock build), and it never hydrates from, or saves over, a load
 * that failed: an empty or sample list saved there would replace the tenant's
 * real catalogue.
 */
export function KnowledgeBasePage() {
  const [products, setProducts] = useState<Product[]>(USE_MOCKS ? seedProducts : []);
  const [faqs, setFaqs] = useState<FaqEntry[]>(USE_MOCKS ? seedFaqs : []);
  const [modalMode, setModalMode] = useState<ProductModalMode>(null);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);

  // Load products (chatbot productsJson) + FAQs (faqs table); hydrate once, then
  // debounce-persist the product list and push FAQ changes per-item. Mock fallback.
  const { loadedProducts, loadedFaqs, loadFailed, businessKnowledge, isLoading, persistProducts, saveBusinessKnowledge, isSavingBusinessKnowledge, createFaq, updateFaq, deleteFaq } = useKnowledge();
  const hydratedRef = useRef(false);
  // The last list known to match the server: what was loaded, then what was last sent. Auto-save
  // skips a list that is still that exact array, so applying the loaded list does not "save" it
  // straight back (a PUT that also rebuilds the AI index, a paid embeddings call, on every visit).
  // ⚠️ Compared by reference, NOT a one-shot "skip next" flag: when applying the loaded list
  // changes nothing (setState with an equal value does not re-render), such a flag stays set and
  // swallows the user's first real edit, which is then silently never saved.
  const lastSyncedRef = useRef<Product[] | null>(null);
  useEffect(() => {
    // Not hydrated means not auto-saved (see the effect below), which is what a failed load needs.
    if (isLoading || loadFailed || hydratedRef.current) return;
    lastSyncedRef.current = loadedProducts;
    setProducts(loadedProducts);
    setFaqs(loadedFaqs);
    hydratedRef.current = true;
  }, [isLoading, loadFailed, loadedProducts, loadedFaqs]);
  useEffect(() => {
    if (loadFailed) {
      toast.error("Your knowledge base didn't load, so changes here won't be saved. Refresh to try again.", {
        id: 'knowledge-load-failed',
      });
    }
  }, [loadFailed]);
  useEffect(() => {
    if (!hydratedRef.current || products === lastSyncedRef.current) return;
    const t = setTimeout(() => {
      lastSyncedRef.current = products;
      persistProducts(products);
    }, 700);
    return () => clearTimeout(t);
  }, [products, persistProducts]);

  const closeModal = () => {
    setModalMode(null);
    setEditingProduct(null);
  };

  // A real re-index: saving the business knowledge rebuilds the AI's whole index (business text
  // and products) on the server. This used to be a timer that only showed a success toast.
  const reindex = () => saveBusinessKnowledge(businessKnowledge);
  const handleReindex = () => {
    toast.promise(reindex(), {
      loading: 'Re-indexing your knowledge base…',
      success: 'Knowledge base re-indexed',
      error: 'Re-indexing failed. Try again in a moment.',
    });
  };

  // Let a Copilot action open a product modal or trigger a re-index after it
  // navigates here (payloads are buffered by the bus until this page mounts).
  useCopilotCommand(CopilotCommand.KnowledgeOpenModal, (payload) => {
    if (payload === 'add' || payload === 'csv' || payload === 'sheet') {
      setModalMode(payload);
    }
  });
  useCopilotCommand(CopilotCommand.KnowledgeReindex, () => {
    handleReindex();
  });

  const handleSaveProduct = (product: Product) => {
    setProducts((prev) => {
      const exists = prev.some((item) => item.id === product.id);
      return exists ? prev.map((item) => (item.id === product.id ? product : item)) : [product, ...prev];
    });
  };

  const handleImportProducts = (imported: Product[]) => {
    setProducts((prev) => [...imported, ...prev]);
  };

  const handleToggleProductInKnowledgeBase = (id: string) => {
    setProducts((prev) =>
      prev.map((product) => (product.id === id ? { ...product, inKnowledgeBase: !product.inKnowledgeBase } : product))
    );
  };

  const handleDeleteProduct = (id: string) => {
    setProducts((prev) => prev.filter((product) => product.id !== id));
    toast.success('Product removed');
  };

  const handleAddFaq = (entry: Pick<FaqEntry, 'question' | 'answer'>) => {
    const tempId = `faq-${Date.now()}`;
    setFaqs((prev) => [{ id: tempId, inKnowledgeBase: true, ...entry }, ...prev]);
    // Persist and swap the temp id for the real one on success.
    createFaq(entry)
      .then((real) => {
        if (real?.id) setFaqs((prev) => prev.map((faq) => (faq.id === tempId ? { ...faq, id: real.id } : faq)));
      })
      .catch(() => {});
  };

  const handleUpdateFaq = (id: string, updates: Partial<FaqEntry>) => {
    setFaqs((prev) => prev.map((faq) => (faq.id === id ? { ...faq, ...updates } : faq)));
    updateFaq({ id, patch: updates });
  };

  const handleDeleteFaq = (id: string) => {
    setFaqs((prev) => prev.filter((faq) => faq.id !== id));
    deleteFaq(id);
    toast.success('Removed from AI knowledge');
  };

  const handleToggleFaqInKnowledgeBase = (id: string) => {
    const nextVal = !(faqs.find((faq) => faq.id === id)?.inKnowledgeBase ?? true);
    setFaqs((prev) => prev.map((faq) => (faq.id === id ? { ...faq, inKnowledgeBase: nextVal } : faq)));
    updateFaq({ id, patch: { inKnowledgeBase: nextVal } });
  };

  return (
    <MotionConfig reducedMotion="user">
      <div className="flex-1 bg-transparent p-4 md:p-6">
        <div className="mx-auto max-w-7xl space-y-4 md:space-y-5">
          <div data-copilot-target="knowledge-summary">
            <KnowledgeSummaryStrip
              productsInKnowledgeBase={countInKnowledgeBase(products, faqs)}
              faqCount={faqs.length}
              missingDescriptions={countProductsMissingDescriptions(products)}
              lastReindexAt={null}
              onReindex={reindex}
            />
          </div>

          <div data-copilot-target="knowledge-business-docs">
            <BusinessKnowledgeCard
              value={businessKnowledge}
              onSave={saveBusinessKnowledge}
              isSaving={isSavingBusinessKnowledge}
            />
          </div>

          <div data-copilot-target="knowledge-sources">
            <ProductSourcesGrid
              products={products}
              onAddManual={() => setModalMode('add')}
              onImportCsv={() => setModalMode('csv')}
              onConnectSheet={() => setModalMode('sheet')}
            />
          </div>

          <ProductsTable
            products={products}
            onToggleInKnowledgeBase={handleToggleProductInKnowledgeBase}
            onEdit={(product) => {
              setEditingProduct(product);
              setModalMode('edit');
            }}
            onDelete={handleDeleteProduct}
          />

          <div data-copilot-target="knowledge-faq">
            <FaqSection
              faqs={faqs}
              onAdd={handleAddFaq}
              onUpdate={handleUpdateFaq}
              onDelete={handleDeleteFaq}
              onToggleInKnowledgeBase={handleToggleFaqInKnowledgeBase}
            />
          </div>
        </div>
      </div>

      <AddProductModal
        open={modalMode === 'add' || modalMode === 'edit'}
        onClose={closeModal}
        onSave={handleSaveProduct}
        initialProduct={modalMode === 'edit' ? editingProduct : null}
      />
      <ImportCsvModal open={modalMode === 'csv'} onClose={closeModal} onImport={handleImportProducts} />
      <ConnectSheetModal open={modalMode === 'sheet'} onClose={closeModal} onConnect={handleImportProducts} />
    </MotionConfig>
  );
}

export default KnowledgeBasePage;
